import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from "@angular/forms";
import {
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import {
  IInputCalendarComponentOptions,
  NInputCalendarPopoverComponent
} from "./n-input-calendar-popover/n-input-calendar-popover.component";
import {BehaviorSubject} from "rxjs";
import {dateEquals, parseStringDate, truncateTime} from "../../helpers";
// noinspection TypeScriptPreferShortImport
import {isMobile, isTouch} from "../utils";
// noinspection TypeScriptPreferShortImport
import {NInputComponent} from "../n-input/n-input.component";
// noinspection TypeScriptPreferShortImport
import {PopoverInstance} from "../../n-popover/PopoverInstance";
// noinspection TypeScriptPreferShortImport
import {PopoverService} from "../../n-popover/PopoverService";
import {NInputErrorMessages} from "../public_components";


@Component({
  selector: "n-input-calendar",
  templateUrl: "./n-input-calendar.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: [
    "./n-input-calendar.css"
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NInputCalendarComponent),
      multi: true
    }, {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NInputCalendarComponent),
      multi: true
    }
  ],
})
export class NInputCalendarComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy, OnChanges {

  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() required: boolean;
  @Input() showIcon: boolean;
  @Input() clearable: boolean;
  @Input() navigate: boolean;
  /**
   * Custom error message for input
   */
  @Input() errorMessages: NInputErrorMessages;

  @Input() textAlignLeft = false;

  @Input() customDateFormatter: (d: Date) => string;

  @ViewChild("wrapper", {read: ElementRef, static: true})
  wrapper: ElementRef;

  @ViewChild("nInput", {static: true}) nInput: NInputComponent;

  date$ = new BehaviorSubject<Date>(undefined);

  minDateToSelect: Date;
  maxDateToSelect: Date;

  inputControl: FormControl;
  isNative: boolean;
  isTouch: boolean;
  readonly wide: boolean;
  readonly type: string;
  canNavPrev$ = new BehaviorSubject<boolean>(true);
  canNavForward$ = new BehaviorSubject<boolean>(true);
  private propagateChange: (_) => void;
  private propagateValidation: () => void;
  private popover: PopoverInstance<any>;
  private isMouseOverPopover: boolean;
  private touchedFn: () => void;

  constructor(private popoverSvc: PopoverService,
              private elementRef: ElementRef,
              private renderer: Renderer2,
              private cd: ChangeDetectorRef,
              @Attribute("wide") wide: string) {

    this.wide = wide !== null;
    this.isNative = isMobile();
    this.isTouch = isTouch();

    this.type = this.isNative ? "date" : "text";

    if (this.isNative) {
      this.inputControl = new FormControl("");
    } else {
      this.inputControl = new FormControl("", {updateOn: "blur"});
    }

    this.propagateValidation = () => {
    };
  }

  private _disabled: boolean;

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(val: boolean) { // deprecated
    this.setDisabledState(val);
  }

  @Input()
  set isDisabled(val: boolean) {
    this.setDisabledState(val);
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
    this._disabled = isDisabled;
    this.cd.markForCheck();
  }

  ngOnInit() {
    this.adjustDatesToSelect();

    this.inputControl.valueChanges.subscribe(v => {
      if (v === "") {

        if (this.navigate && this.date$.value) {
          // navigate mode does not allow empty text field
          this.updateTextField(this.date$.value);
          return;
        }

        if (this.clearable) {
          this.updateTextField(null);
        }

        this.updateModel(null);
        this.date$.next(null);
        return;
      }
      if (!this.isNative) {
        const newDate = parseStringDate(v);
        if (newDate && this.isValidDateRange(newDate)) {
          if (this.date$.value) {
            if (!dateEquals(this.date$.value, newDate)) {
              this.date$.next(newDate);
              this.updateModel(newDate);
            }
          } else {
            this.date$.next(newDate);
            this.updateModel(newDate);
          }
        }
      } else {
        // native version
        if (v instanceof Date) {
          this.updateModel(v);
          this.date$.next(v);
        } else {
          const newDate = parseStringDate(v as string, true);
          this.updateModel(newDate);
          this.date$.next(newDate);
        }
      }
    });
  }

  ngOnDestroy() {
    this.hidePicker();
  }

  writeValue(val: Date): void {
    this.date$.next(val);
    this.updateTextField(val);
    this.invalidateStyles();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.touchedFn = fn;
  }

  ngOnChanges(changes: SimpleChanges) {
    const {minDate, maxDate} = changes;

    if ((minDate && !minDate.isFirstChange()) || (maxDate && !maxDate.isFirstChange())) {
      this.adjustDatesToSelect();
    }
  }

  isValidDateRange(date: Date) {
    return (!this.minDateToSelect || this.minDateToSelect <= date) && (!this.maxDateToSelect || this.maxDateToSelect >= date);
  }

  showPicker() {
    if (this.popover || this.isNative) {
      return;
    }
    if (this.isTouch) {
      this.elementRef.nativeElement.childNodes[0].focus();
    }

    const changeEmitter = new EventEmitter<Date>();
    changeEmitter.subscribe(date => {
      date = date ? truncateTime(date) : undefined;
      this.updateTextField(date);

      this.updateModel(date);
      this.hidePicker();
      this.date$.next(date);

      this.cd.markForCheck();
    });

    const data: IInputCalendarComponentOptions = {
      change: changeEmitter,
      date$: this.date$,
      maxDate: this.maxDateToSelect,
      minDate: this.minDateToSelect
    };

    this.popover = this.popoverSvc.open({
      componentType: NInputCalendarPopoverComponent,
      autoclose: true,
      mobileView: false,
      popoverData: data,
      position: "bottom",
      hideArrow: true,
      horizontalAlign: "left",
      targetElementRef: this.wrapper,
    });

    this.popover.mouseover.subscribe(() => {
      this.isMouseOverPopover = true;
    });
    this.popover.mouseleave.subscribe(() => {
      this.isMouseOverPopover = false;
    });
    this.popover.onClose.subscribe(() => {
      this.popover = null;
      this.isMouseOverPopover = false;
    });
  }

  hidePicker() {
    if (!this.popover) {
      return;
    }
    this.popover.done(undefined);
  }

  onBlur() {
    this.propagateTouched();
    if (this.isNative) {
      return;
    }

    const inputVal: string | Date = this.inputControl.value;
    if (!(inputVal instanceof Date)) {

      const newDate = parseStringDate(this.inputControl.value);
      if (!newDate || !this.isValidDateRange(newDate)) {
        if (this.navigate || this.clearable) {
          this.updateTextField(this.date$.value);
        } else if (this.customDateFormatter) {
          this.updateTextField(null);
        } else {
          if (!newDate) {
            this.inputControl.setErrors({"datepicker-format": {"message": "date should be formatted as «24.12.2007»"}});
          } else if (!this.isValidDateRange(newDate)) {
            this.inputControl.setErrors({
              "datepicker-range": {
                message: "selected date is outside of defined bounds",
                minDate: this.minDate,
                maxDate: this.maxDate,
              }
            });
          }

          this.updateModel(null);
        }

      }

      if (!this.isMouseOverPopover) {
        this.hidePicker();
      }
    }
  }

  prevDate() {
    if (!this.date$.value) {
      return;
    }
    const newDate = getPrevDate(this.date$.value);
    this.updateDate(newDate);
  }

  nextDate() {
    if (!this.date$.value) {
      return;
    }
    const newDate = getNextDate(this.date$.value);
    this.updateDate(newDate);
  }

  formatDate(d: Date, isoFormat: boolean): string {
    if (this.customDateFormatter) {
      return this.customDateFormatter(d);
    }

    if (d) {
      const pad = (n: number): string => {
        return n < 10 ? "0" + n.toString() : n.toString();
      };
      if (isoFormat) {
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      }
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    } else {
      return "";
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.propagateValidation = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.inputControl.errors;
  }

  private updateTextField(val: Date) {
    val = val ? truncateTime(val) : undefined;
    this.inputControl.setValue(this.formatDate(val, this.isNative), {emitEvent: false});
    if (this.navigate) {
      this.updateCanNavigate(val);
    }
  }

  private updateCanNavigate(val: Date) {
    this.canNavPrev$.next(!val || !this.minDate || this.minDateToSelect.getTime() < val.getTime());
    this.canNavForward$.next(!val || !this.maxDate || this.maxDateToSelect.getTime() > val.getTime());
  }

  private updateModel(val: Date) {
    if (this.propagateChange) {
      this.propagateChange(val);
    }
    this.invalidateStyles();
  }

  private adjustDatesToSelect() {
    this.minDateToSelect = this.minDate ? truncateTime(this.minDate) : undefined;
    this.maxDateToSelect = this.maxDate ? truncateTime(this.maxDate) : undefined;

    if (this.isNative) {
      const inputElem = this.nInput.getInputElement();

      if (this.minDateToSelect) {
        this.renderer.setAttribute(inputElem, "min", this.formatDate(this.minDateToSelect, true));
      } else {
        this.renderer.removeAttribute(inputElem, "min");
      }

      if (this.maxDateToSelect) {
        this.renderer.setAttribute(inputElem, "max", this.formatDate(this.maxDateToSelect, true));
      } else {
        this.renderer.removeAttribute(inputElem, "max");
      }

    }


    this.updateCanNavigate(this.date$.value);
  }

  private invalidateStyles() {
    const cls = "input-calendar__empty";
    if (!this.inputControl.value) {
      this.renderer.addClass(this.elementRef.nativeElement, cls);
    } else {
      this.renderer.removeClass(this.elementRef.nativeElement, cls);
    }
  }

  private propagateTouched() {
    if (this.touchedFn) {
      this.touchedFn();
    }
  }

  private updateDate(date: Date) {
    this.updateTextField(date);
    this.date$.next(date);
    this.updateModel(date);
  }
}


function getNextDate(date: Date): Date {
  const tempDate = new Date(date.getTime());
  tempDate.setDate(tempDate.getDate() + 1);
  return tempDate;
}

function getPrevDate(date: Date): Date {
  const tempDate = new Date(date.getTime());
  tempDate.setDate(tempDate.getDate() - 1);
  return tempDate;
}
